/**
 * Home Intro — Pembleton wordmark letter stagger animation.
 * Oversized wordmark anchored to the bottom of the viewport.
 * Letters animate upward on page load with stagger.
 * Respects prefers-reduced-motion. Falls back gracefully if GSAP unavailable.
 */

let currentTimeline = null;
let resizeHandler = null;

/**
 * Scale the wordmark font-size so it spans exactly the viewport width.
 * Measures the natural text width at the current font-size, then adjusts.
 */
function scaleWordmark(h1) {
    // Reset to CSS default so we measure from a clean base
    h1.style.fontSize = '';

    const viewportWidth = window.innerWidth;
    const currentFontSize = parseFloat(getComputedStyle(h1).fontSize);
    const naturalWidth = h1.scrollWidth;

    if (naturalWidth > 0 && currentFontSize > 0) {
        const scaledSize = (viewportWidth / naturalWidth) * currentFontSize;
        h1.style.fontSize = `${scaledSize}px`;
    }
}

/**
 * Find the currently visible wordmark (desktop or mobile).
 */
function getVisibleWordmark(section) {
    const wordmarks = section.querySelectorAll('.home-intro__wordmark');
    for (const h1 of wordmarks) {
        if (h1.offsetParent !== null || getComputedStyle(h1.parentElement).display !== 'none') {
            // Check if the parent container is visible
            if (getComputedStyle(h1.parentElement).display !== 'none') return h1;
        }
    }
    return wordmarks[0];
}

/**
 * Scale the wordmark to fill the viewport width.
 * Waits for fonts to load so measurements are accurate.
 * Called immediately (before GSAP loads) and on Swup page:view.
 */
export function scaleHomeIntro() {
    const section = document.getElementById('home-intro');
    if (!section) return;

    // Scale all wordmarks (both desktop and mobile)
    const wordmarks = section.querySelectorAll('.home-intro__wordmark');

    // Wait for fonts before measuring
    document.fonts.ready.then(() => {
        wordmarks.forEach((h1) => scaleWordmark(h1));
    });

    // Set up resize listener (replace any previous)
    if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
    }
    let resizeTimeout;
    resizeHandler = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            wordmarks.forEach((h1) => scaleWordmark(h1));
        }, 150);
    };
    window.addEventListener('resize', resizeHandler);
}

/**
 * Animate the wordmark letters. Called after GSAP loads.
 */
export function initHomeIntro() {
    const section = document.getElementById('home-intro');
    if (!section) return;

    // Kill any in-progress timeline (Swup re-navigation)
    if (currentTimeline) {
        currentTimeline.kill();
        currentTimeline = null;
    }

    const gsap = window.gsap;
    if (!gsap) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const letters = section.querySelectorAll('[data-letter]');

    if (reducedMotion || !letters.length) {
        gsap.set(letters, { opacity: 1, y: 0 });
        return;
    }

    // Set initial state via GSAP transform (not CSS translate, which conflicts)
    gsap.set(letters, { y: '110%' });

    // Animate letters up from below
    const tl = gsap.timeline({ delay: 0.3 });

    tl.to(letters, {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.06,
        ease: 'power3.out',
    });

    currentTimeline = tl;

    // Wordmark fade + blur as the user scrolls past the hero
    initHomeIntroScrollFade(gsap, reducedMotion);

    return tl;
}

/**
 * Fade and blur the fixed wordmark wrappers as the user scrolls
 * past the hero. Fully invisible after 150vh of scroll from page top
 * (1 hero height + 0.5 viewport height of additional scroll).
 */
export function initHomeIntroScrollFade(gsap, reducedMotion) {
    if (!window.ScrollTrigger) return;

    const targets = [
        document.getElementById('home-intro__fixed--desktop'),
        document.getElementById('home-intro__fixed--mobile'),
    ].filter(Boolean);

    if (!targets.length) return;

    // Hint the compositor — opacity is already cheap, but blur benefits.
    gsap.set(targets, { willChange: 'filter, opacity' });

    if (reducedMotion) {
        // Honour reduced motion: still fade out (so content doesn't sit on
        // top of a fully-opaque wordmark) but skip the blur.
        gsap.to(targets, {
            opacity: 0,
            ease: 'none',
            scrollTrigger: {
                trigger: '#home-intro',
                start: 'top top',
                end: '+=100%',
                scrub: true,
            },
        });
        return;
    }

    gsap.to(targets, {
        opacity: 0,
        filter: 'blur(24px)',
        ease: 'none',
        scrollTrigger: {
            trigger: '#home-intro',
            start: 'top top',
            end: '+=100%',
            scrub: true,
        },
    });
}
